import { toMetricMM } from './utils';
import type { DeckInterface as MotionScreenDeckInterface } from 'unitData/CircularMotionScreen/CircularMotionScreenTypes';
import { ScreenCalcMethods } from 'store/settings/types';
import type { SimulationResponseUnitInfoInterface } from 'store/simulation/types';
import type { DeckInterface as MultislopeDeckInterface } from 'unitData/MultislopeScreen/MultislopeScreenTypes';
import type { KwataniScreenDeckInterface } from 'unitData/KwataniScreen/KwataniScreenTypes';
import type { FreeFallDeckInterface } from 'unitData/FreeFallScreen/sharedTypes';
import type { BananaScreenDeckInterface } from 'unitData/BananaScreen/BananaScreenTypes';
import type { GenericScreenDeckInterface } from 'unitData/Generic/GenericScreen/GenericScreenTypes';
import type { GrizzlyScreenDeckInterface } from 'unitData/GrizzlyScreenSG/GrizzlyScreenTypes';
import type { GrizzlyScreenDeck } from 'store/units/types';
import type { FeederDeckInterface } from 'unitData/Feeders/FeederScreen/FeederScreenTypes';

export type DeckPropertyConfig<
  TDeck extends { display: Record<string, boolean> },
> = {
  key: keyof TDeck['display'];
  label: string;
  getText: (deck: TDeck) => string;
};

export type DeckDisplayConfig<
  TDeck extends { display: Record<string, boolean> },
> = {
  properties: DeckPropertyConfig<TDeck>[];
};

export type DeckDisplayConfigWithModes<
  TDeck extends { display: Record<string, boolean> },
  TContext = any,
> = {
  getProperties: (context: TContext) => DeckPropertyConfig<TDeck>[];
};

export const createSeparationSizeProperty = <
  TDeck extends { display: Record<string, boolean>; separationSize?: number },
>(): DeckPropertyConfig<TDeck> => ({
  key: 'separationSize',
  label: 'Separation size',
  getText: (deck) => toMetricMM('Sep:', deck.separationSize, { decimals: 4 }),
});

export const createScreeningMediaProperties = <
  TDeck extends {
    display: Record<string, boolean>;
    screeningMedia?: {
      model?: string;
      description?: string;
      material?: string;
      type?: string;
      function?: string;
    };
  },
>(): DeckPropertyConfig<TDeck>[] => [
  {
    key: 'model',
    label: 'Media model',
    getText: (deck) => deck.screeningMedia?.model ?? '',
  },
  {
    key: 'description',
    label: 'Description',
    getText: (deck) => deck.screeningMedia?.description ?? '',
  },
  {
    key: 'material',
    label: 'Material',
    getText: (deck) => deck.screeningMedia?.material ?? '',
  },
  {
    key: 'type',
    label: 'Type',
    getText: (deck) => deck.screeningMedia?.type ?? '',
  },
  {
    key: 'function',
    label: 'Function',
    getText: (deck) => deck.screeningMedia?.function ?? '',
  },
];

export const createOpenAreaProperty = <
  TDeck extends { display: Record<string, boolean>; openArea?: number },
>(): DeckPropertyConfig<TDeck> => ({
  key: 'openArea',
  label: 'Open area',
  getText: (deck) => `Open area: ${deck?.openArea} %`,
});

export type MotionScreenContext = {
  screenCalcMethod: string;
  simunitInfo?: SimulationResponseUnitInfoInterface;
  deckIndex: number;
};

export const motionScreenDeckConfig: DeckDisplayConfigWithModes<
  MotionScreenDeckInterface,
  MotionScreenContext
> = {
  getProperties: (context: MotionScreenContext) => {
    if (context.screenCalcMethod === ScreenCalcMethods.NORMAL.value) {
      return [
        createSeparationSizeProperty<MotionScreenDeckInterface>(),
        ...createScreeningMediaProperties<MotionScreenDeckInterface>(),
      ];
    } else {
      const efficiency =
        context.simunitInfo?.decks?.[context.deckIndex]?.efficiency ?? 0;

      return [
        createSeparationSizeProperty<MotionScreenDeckInterface>(),
        {
          key: 'model',
          label: 'Media model',
          getText: (deck) => deck.screeningElement?.name || '',
        },
        createOpenAreaProperty<MotionScreenDeckInterface>(),
        {
          key: 'particleShape',
          label: 'Particle shape',
          getText: (deck) =>
            `Particle shape: ${deck?.particleShape?.name || ''}`,
        },
        {
          key: 'efficiency',
          label: 'Efficiency',
          getText: () => `Efficiency: ${Math.round(efficiency)} %`,
        },
      ];
    }
  },
};

export const multislopeScreenDeckConfig: DeckDisplayConfig<MultislopeDeckInterface> =
  {
    properties: [
      createSeparationSizeProperty<MultislopeDeckInterface>(),
      ...createScreeningMediaProperties<MultislopeDeckInterface>(),
      {
        key: 'apertureRatio',
        label: 'Aperture ratio',
        getText: (deck) => `Aperture ratio: ${deck?.apertureRatio}`,
      },
      createOpenAreaProperty<MultislopeDeckInterface>(),
      {
        key: 'particleShape',
        label: 'Particle shape',
        getText: (deck) => `Particle shape 3:1: ${deck?.particleShape} %`,
      },
      {
        key: 'efficiency',
        label: 'Efficiency',
        getText: (deck) => `Efficiency: ${deck?.efficiencyNumber} %`,
      },
      {
        key: 'typeOfApplication',
        label: 'Type of application',
        getText: (deck) =>
          `Type of application: ${deck?.typeOfApplication.name}`,
      },
      {
        key: 'sprayBar',
        label: 'Spray bar',
        getText: (deck) => `Spray bar: ${deck?.sprayBar ? 'Yes' : 'No'}`,
      },
    ],
  };

export const kwataniScreenDeckConfig: DeckDisplayConfig<KwataniScreenDeckInterface> =
  {
    properties: [
      createSeparationSizeProperty<KwataniScreenDeckInterface>(),
      {
        key: 'shape',
        label: 'Aperture shape',
        getText: (deck) =>
          `Aperture shape: ${deck.screeningElement?.name || ''}`,
      },
      ...createScreeningMediaProperties<KwataniScreenDeckInterface>(),
      createOpenAreaProperty<KwataniScreenDeckInterface>(),
      {
        key: 'particleShape',
        label: 'Particle shape',
        getText: (deck) => `Particle shape: ${deck?.particleShape?.name || ''}`,
      },
      {
        key: 'efficiency',
        label: 'Efficiency',
        getText: (deck) => `Efficiency: ${deck?.efficiency?.name || ''}`,
      },
    ],
  };

export const freeFallScreenDeckConfig: DeckDisplayConfig<FreeFallDeckInterface> =
  {
    properties: [
      createSeparationSizeProperty<FreeFallDeckInterface>(),
      {
        key: 'screeningElement',
        label: 'Screening element',
        getText: (deck) => deck.screeningMediaId.name,
      },
    ],
  };

export const bananaScreenDeckConfig: DeckDisplayConfig<BananaScreenDeckInterface> =
  {
    properties: [createSeparationSizeProperty<BananaScreenDeckInterface>()],
  };

export const genericScreenDeckConfig: DeckDisplayConfig<GenericScreenDeckInterface> =
  {
    properties: [createSeparationSizeProperty<GenericScreenDeckInterface>()],
  };

export type GrizzlyScreenContext = {
  deckIndex: number;
};

export const grizzlyScreenDeckConfig: DeckDisplayConfigWithModes<
  GrizzlyScreenDeckInterface,
  GrizzlyScreenContext
> = {
  getProperties: (context: GrizzlyScreenContext) => {
    // Deck 0 is the grizzly deck - only show separation size
    // Deck 1 is the separation deck - show both separation size and screening element
    if (context.deckIndex === 0) {
      return [createSeparationSizeProperty<GrizzlyScreenDeckInterface>()];
    } else {
      return [
        createSeparationSizeProperty<GrizzlyScreenDeckInterface>(),
        {
          key: 'screeningElement',
          label: 'Screening element',
          getText: (deck) => deck.screeningElement?.name || '',
        },
      ];
    }
  },
};

export const grizzlyScreenDeckNames = ['Grizzly deck', 'Separation deck'];

export const grizzlyFeederDeckConfig: DeckDisplayConfig<GrizzlyScreenDeck> = {
  properties: [createSeparationSizeProperty<GrizzlyScreenDeck>()],
};

export const grizzlyFeederDeckNames = ['Deck 1', 'Deck 2'];

export const feederScreenDeckConfig: DeckDisplayConfig<FeederDeckInterface> = {
  properties: [createSeparationSizeProperty<FeederDeckInterface>()],
};

export const feederScreenDeckNames = ['Deck 1', 'Deck 2'];
